import React, { Component } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { throttle, debounce } from "throttle-debounce";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import userActions from "../../../_actions/userActions";
import unitActions from "../../../_actions/unitActions";
import distOptions from "../../../_constants/distOptions";
import Table from "../../_common/Table";
import AdminUsersDetails from "./AdminUsersDetails";
import { USER } from "../../../_constants/actionConstants";
import "./adminUsers.css";
import AddUserModalV2 from "./AddUserModalV2";

class AdminUsers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      distFilter: null,
      unitFilter: null,
      userFilter: "",
      sortColumn: { path: "fullName", order: "asc" },
      selectedUser: null,
      showEditUserModal: false,
      isNewUser: false,
      currPage: 1,
      pageCount: 1,
    };

    this.columns = [
      {
        path: "fullName",
        label: "Name",
        thClassName: "admin-users-table__th--name",
        content: (user) => <b>{user.fullName}</b>,
      },
      {
        path: "employeeNo",
        label: "S#",
        thClassName: "admin-users-table__th--employeeNo",
        content: (user) => <small>{user.employeeNo}</small>,
      },
      {
        path: "userRoleDescription",
        label: "Title",
        thClassName: "admin-users-table__th--title",
        content: (user) => this.getUserRoleDescription(user.userRoleId),
      },
      {
        path: "distUnit",
        label: "Unit",
        thClassName: "admin-users-table__th--distUnit",
        content: (user) => this.getUserDistUnit(user.unitId),
      },
    ];

    this.autocompleteThrottled = throttle(250, this.handleSearch);
    this.autocompleteDebounced = debounce(250, this.handleSearch);
  }

  async componentDidMount() {
    const { dispatch, tabId } = this.props;
    dispatch(unitActions.getAll(null, tabId));
    const pageCount = await dispatch(userActions.getUsersPagedV2(tabId, 1, 50, true));
    this.setState({ pageCount });
  }

  componentWillUnmount() {
    const { dispatch, tabId } = this.props;
    dispatch({ type: USER.CLEAR_USER_LIST, tabId });
  }

  // Called upon successful user registration or edit in editUserModal.
  editUserModalCallback = async () => {
    const { selectedUser, isNewUser } = this.state;
    this.refreshUserList();

    // If new user was just added, clear current selectedUser.
    // Else (i.e. after editing), if selectedUser exists (which most likely it does,
    // since user must've first selected a user to edit), update it - otherwise
    // UserDetails panel will still show stale info.
    this.setState({
      selectedUser: isNewUser
        ? null
        : selectedUser
        ? this.props.userList.find((u) => u.userId === selectedUser.userId)
        : null,
    });
  };

  refreshUserList = async (clearSelectedUser = true, resetList = true) => {
    const { dispatch, tabId } = this.props;
    const { distFilter, unitFilter, userFilter, currPage } = this.state;
    const district = distFilter ? distFilter.value : null;
    const unitId = unitFilter ? unitFilter.value : null;
    const filters = { district, unitId, searchString: userFilter };
    const pageCount = await dispatch(
      userActions.getUsersPagedV2(tabId, currPage, 50, resetList, filters)
    );
    this.setState({
      pageCount,
      selectedUser: clearSelectedUser ? null : this.state.selectedUser,
    });
  };

  userList_getNextPage = () => {
    const { currPage, distFilter, unitFilter, userFilter } = this.state;
    const { dispatch, tabId } = this.props;
    this.setState({ currPage: currPage + 1 }, () => {
      const district = distFilter ? distFilter.value : null;
      const unitId = unitFilter ? unitFilter.value : null;
      const filters = { district, unitId, searchString: userFilter };
      dispatch(
        userActions.getUsersPagedV2(tabId, this.state.currPage, 50, false, filters)
      );
    });
  };

  handleSearch = async () => {
    const { dispatch, tabId } = this.props;
    const { distFilter, unitFilter, userFilter } = this.state;
    dispatch({ type: USER.SET_USER_SEARCH_WAITINGFOR, waitingFor: userFilter, tabId });
    const district = distFilter ? distFilter.value : null;
    const unitId = unitFilter ? unitFilter.value : null;
    const filters = { district, unitId, searchString: userFilter };
    const pageCount = dispatch(userActions.getUsersPagedV2(tabId, 1, 50, true, filters));
    this.setState({ currPage: 1, pageCount });
  };

  handleSort = (sortColumn) => this.setState({ sortColumn });

  /**
   * If distFilter is changed, clear the unit filter (unless same district was selected.)
   * If unitFilter is changed, update distFilter to the district that the selected
   * unit belongs to.
   * @param {string} fieldName - either 'distFilter' or 'unitFilter'
   */
  handleChange_selectFilter = (fieldName) => async (selectedOption) => {
    const { dispatch, tabId } = this.props;
    const { distFilter, unitFilter, userFilter } = this.state;
    if (fieldName === "distFilter") {
      // If a district is selected:
      if (selectedOption && selectedOption.value > 0) {
        const district = selectedOption.value;
        const distDidNotChange = distFilter && distFilter.value === selectedOption.value;
        const unitId = distDidNotChange && unitFilter ? unitFilter.value : null;
        const filters = { district, unitId, searchString: userFilter };
        const pageCount = await dispatch(
          userActions.getUsersPagedV2(tabId, 1, 50, true, filters)
        );

        this.setState({
          distFilter: selectedOption,
          unitFilter: distDidNotChange ? unitFilter : null,
          selectedUser: null,
          currPage: 1,
          pageCount,
        });
      }
      // If distFilter is cleared:
      else {
        // Also nullify the unitFilter
        const filters = { district: null, unitId: null, searchString: userFilter };
        const pageCount = await dispatch(
          userActions.getUsersPagedV2(tabId, 1, 50, true, filters)
        );
        this.setState({ distFilter: null, unitFilter: null, currPage: 1, pageCount });
      }
    }
    if (fieldName === "unitFilter") {
      if (selectedOption && selectedOption.value !== 0) {
        const district = distFilter ? distFilter.value : null;
        const unitId = selectedOption.value;
        const unitDidNotChange = unitFilter && unitFilter.value === selectedOption.value;
        const mustChangeDistFilter =
          (distFilter &&
            !unitDidNotChange &&
            selectedOption.district !== distFilter.value) ||
          (!distFilter && selectedOption.district);

        const filters = { district, unitId, searchString: userFilter };
        const pageCount = await dispatch(
          userActions.getUsersPagedV2(tabId, 1, 50, true, filters)
        );

        this.setState({
          distFilter: mustChangeDistFilter
            ? distOptions.with59.find((o) => o.value === selectedOption.district)
            : distFilter,
          unitFilter: selectedOption,
          selectedUser: null,
          currPage: 1,
          pageCount,
        });
      }
      // If unitFilter is cleared:
      else {
        // Keep the existing distFilter.
        const filters = {
          district: distFilter ? distFilter.value : null,
          unitId: null,
          searchString: userFilter,
        };
        const pageCount = await dispatch(
          userActions.getUsersPagedV2(tabId, 1, 50, true, filters)
        );
        this.setState({ unitFilter: null, currPage: 1, pageCount });
      }
    }
  };

  handleChange_userFilter = (e) => {
    this.setState({ userFilter: e.target.value }, async () => {
      const { distFilter, unitFilter, userFilter } = this.state;
      const { dispatch, tabId } = this.props;
      let pageCount = 1;
      if (!userFilter) {
        const district = distFilter ? distFilter.value : null;
        const unitId = unitFilter ? unitFilter.value : null;
        const filters = { district, unitId, searchString: userFilter };
        pageCount = await dispatch(
          userActions.getUsersPagedV2(tabId, 1, 50, true, filters)
        );
      } else if (userFilter.length < 5) this.autocompleteThrottled(userFilter);
      else this.autocompleteDebounced(userFilter);
      this.setState({ currPage: 1, pageCount });
    });
  };

  handleClick_userListTr = (selectedUser) => {
    this.setState({ selectedUser });
  };
  handleClick_addUser = (e) => {
    e.preventDefault();
    this.setState({ isNewUser: true }, this.toggleEditUserModal);
  };
  handleClick_editUser = (e) => {
    e.preventDefault();
    this.setState({ isNewUser: false }, this.toggleEditUserModal);
  };
  toggleEditUserModal = () =>
    this.setState(
      { showEditUserModal: !this.state.showEditUserModal },
      () => !this.state.showEditUserModal && window.scrollTo(0, 0)
    );

  getUserDistUnit = (unitId) => {
    const { unitOptions } = this.props;
    const unit = unitOptions.find((uo) => uo.value === unitId);
    return unit ? unit.label : "(N/A)";
  };

  getUserRoleDescription = (userRoleId) => {
    const { userRoles } = this.props;
    const role = userRoles.find((ur) => ur.userRoleId === userRoleId);
    return role ? role.description : "(N/A)";
  };

  renderTableFilters = () => {
    const { distFilter, userFilter, unitFilter } = this.state;
    const { unitOptions } = this.props;
    return (
      <div className="d-flex flex-row flex-wrap w-100 mb-1 admin-filter-group">
        <div className="admin-filter-item admin-filter-item__select--user-dist pr-2">
          <Select
            name="distFilter"
            id="distFilter"
            className="w-100"
            value={distFilter}
            options={distOptions.with59}
            onChange={this.handleChange_selectFilter("distFilter")}
            placeholder="Dist"
            isSearchable={false}
            isClearable={true}
          />
        </div>
        <div className="admin-filter-item admin-filter-item__select--user-unit pr-2">
          <Select
            name="unitFilter"
            id="unitFilter"
            className="w-100"
            value={unitFilter}
            options={
              distFilter
                ? unitOptions.filter((uo) => uo.district === distFilter.value)
                : unitOptions
            }
            onChange={this.handleChange_selectFilter("unitFilter")}
            placeholder="Unit"
            isSearchable={true}
            isClearable={true}
          />
        </div>
        <div className="admin-filter-item admin-filter-item__input--user flex-grow-1">
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text bg-lightgray">
                <FontAwesomeIcon icon="search" className="fa-fw mx-0 text-muted" />
              </span>
            </div>
            <input
              name="userFilter"
              id="userFilter"
              className="form-control"
              value={userFilter}
              onChange={this.handleChange_userFilter}
              placeholder="Name or S#"
            />
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      sortColumn,
      showEditUserModal,
      selectedUser,
      isNewUser,
      currPage,
      pageCount,
    } = this.state;
    const { tabId, userList } = this.props;

    return (
      <React.Fragment>
        <h2 className="mb-1">Users</h2>
        <hr className="mt-0 mb-2" />
        <div className="d-flex flex-row">
          <div className="admin-list pr-1">
            <div className="form-inline mb-2">
              <button
                type="button"
                name="addUser"
                id="addUser"
                className="btn btn-info mr-2"
                onClick={this.handleClick_addUser}
              >
                <FontAwesomeIcon icon="user-plus" className="fa-fw mr-1" />
                New...
              </button>
            </div>
            {this.renderTableFilters()}
            <div className="admin-list__table--wrapper border">
              <Table
                className="table table-sm table-hover admin-list__table"
                data={userList || []}
                idPropName="userId"
                selectedItemId={selectedUser && selectedUser.userId}
                columns={this.columns}
                sortColumn={sortColumn}
                onSort={this.handleSort}
                disableAllSort={true}
                handleClick_tr={this.handleClick_userListTr}
                isPaginated={true}
                currPage={currPage}
                pageCount={pageCount}
                getNextPage={this.userList_getNextPage}
              />
            </div>
          </div>
          <div className="admin-details">
            <div className="card">
              <div className="card-header">User Details</div>
              <div className="card-body">
                <AdminUsersDetails
                  tabId={tabId}
                  user={selectedUser}
                  getUserRoleDescription={this.getUserRoleDescription}
                  getUserDistUnit={this.getUserDistUnit}
                  handleClick_editUser={this.handleClick_editUser}
                  handleClick_editProjectUnits={this.handleClick_editProjectUnits}
                />
              </div>
            </div>
          </div>
        </div>
        {showEditUserModal && (
          <AddUserModalV2
            tabId={tabId}
            isNewUser={isNewUser}
            user={isNewUser ? null : selectedUser}
            showModal={showEditUserModal}
            toggleModal={this.toggleEditUserModal}
            submitCallback={this.editUserModalCallback}
            allowRegisterAsOfficeAdmin={true}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  adminUser: state.authentication.user,
  userRoles: state.users.userRoles || [],
  unitOptions: _.orderBy(
    (state.users.openUnitLists[ownProps.tabId] || []).map((u) => ({
      value: u.unitId,
      label: u.distUnit,
      district: u.district,
    })),
    "label",
    "asc"
  ),
  userList: state.users.openUserLists[ownProps.tabId] || null,
});

export default connect(mapStateToProps)(AdminUsers);
